import { Dialog, DialogActions } from "#components/Dialog"
import { ErrorAlert } from "#components/ErrorAlert"
import { Button } from "#components/ui/button"
import { useI18n } from "#hooks/use-i18n"
import { homeFinancialSDK } from "#sdk/sdk"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import type {
  CreateExpense,
  Expense,
  UpdateExpense,
} from "@home-finance-manager/shared/types/expenses"
import type { Partner } from "@home-finance-manager/shared/types/partners"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect, useMemo } from "react"
import { Resolver, useForm } from "react-hook-form"
import { ExpenseForm } from "./ExpenseForm"
import { createTranslatedExpenseSchemas, type ExpenseFormData } from "./expense-form-schema"

interface ExpenseDialogsProps {
  showDialog: boolean
  editingExpense: Expense | null
  onCloseDialog: () => void
  categories: ExpenseCategory[]
  partners: Partner[]
  onCreateExpense: (data: CreateExpense) => Promise<void>
  onUpdateExpense: (id: number, data: UpdateExpense) => Promise<void>
  isCreating: boolean
  isUpdating: boolean
  deletingExpense: Expense | null
  onDeleteExpense: (id: number) => Promise<void>
  onCloseDeleteDialog: () => void
  isDeleting: boolean
  error: string | null
  isOwner: boolean
}

export const ExpenseDialogs: React.FC<ExpenseDialogsProps> = ({
  showDialog,
  editingExpense,
  onCloseDialog,
  categories,
  partners,
  onCreateExpense,
  onUpdateExpense,
  isCreating,
  isUpdating,
  deletingExpense,
  onDeleteExpense,
  onCloseDeleteDialog,
  isDeleting,
  error,
  isOwner,
}) => {
  const { t } = useI18n()
  const {
    createExpenseSchema: createExpenseSchemaTranslated,
    updateExpenseSchema: updateExpenseSchemaTranslated,
  } = useMemo(() => createTranslatedExpenseSchemas(t), [t])
  const currentSchema = useMemo(
    () => (editingExpense ? updateExpenseSchemaTranslated : createExpenseSchemaTranslated),
    [editingExpense, createExpenseSchemaTranslated, updateExpenseSchemaTranslated]
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(currentSchema) as unknown as Resolver<ExpenseFormData>,
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      amount: 0,
      recurring: "one-time",
      isVirtual: false,
      isIncome: false,
      nextPaymentDate: new Date().toISOString(),
      expenseCategoryId: undefined,
      splitPreference: "equitable",
      splits: [],
    } as unknown as ExpenseFormData,
  })

  const recurringValue = watch("recurring")

  useEffect(() => {
    if (!showDialog && !editingExpense) {
      const timer = setTimeout(() => {
        reset({
          name: "",
          description: "",
          amount: 0,
          recurring: "one-time",
          isVirtual: false,
          isIncome: false,
          nextPaymentDate: new Date().toISOString(),
          expenseCategoryId: undefined,
          splitPreference: "equitable",
          splits: [],
        } as unknown as ExpenseFormData)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [showDialog, editingExpense, reset])

  useEffect(() => {
    if (editingExpense && showDialog) {
      const populateForm = async () => {
        let splits: Array<{
          partnerId: number
          splitType: "equitable" | "equal" | "custom"
          customPercentage: number | null
        }> = []
        let splitPreference: "equitable" | "equal" | "custom" = "equitable"

        // Use splitPreference from editingExpense as fallback
        if (
          editingExpense.splitPreference &&
          (editingExpense.splitPreference === "equitable" ||
            editingExpense.splitPreference === "equal" ||
            editingExpense.splitPreference === "custom")
        ) {
          splitPreference = editingExpense.splitPreference
        }

        try {
          const expenseWithSplits = await homeFinancialSDK.accounts.expenses.getExpenseWithSplits(
            editingExpense.id
          )
          if (expenseWithSplits.expenseSplits && expenseWithSplits.expenseSplits.length > 0) {
            splits = expenseWithSplits.expenseSplits.map((split) => ({
              partnerId: split.partnerId,
              splitType: split.splitType,
              customPercentage: split.customPercentage,
            }))

            if (
              expenseWithSplits.splitPreference &&
              (expenseWithSplits.splitPreference === "equitable" ||
                expenseWithSplits.splitPreference === "equal" ||
                expenseWithSplits.splitPreference === "custom")
            ) {
              splitPreference = expenseWithSplits.splitPreference
            } else {
              const splitTypes = new Set(splits.map((s) => s.splitType))
              if (splitTypes.has("custom")) {
                splitPreference = "custom"
              } else if (splitTypes.has("equitable")) {
                splitPreference = "equitable"
              } else if (splitTypes.has("equal")) {
                splitPreference = "equal"
              }
            }
          } else {
            // If no splits, use default splits based on splitPreference
            const defaultSplitType =
              splitPreference === "custom"
                ? "custom"
                : splitPreference === "equal"
                  ? "equal"
                  : "equitable"
            splits = partners.map((partner) => ({
              partnerId: partner.id,
              splitType: defaultSplitType,
              customPercentage: null,
            }))
          }
        } catch (error) {
          console.error("Failed to fetch expense splits:", error)
          // On error, use default splits based on splitPreference
          const defaultSplitType =
            splitPreference === "custom"
              ? "custom"
              : splitPreference === "equal"
                ? "equal"
                : "equitable"
          splits = partners.map((partner) => ({
            partnerId: partner.id,
            splitType: defaultSplitType,
            customPercentage: null,
          }))
        }

        reset({
          name: editingExpense.name,
          description: editingExpense.description || "",
          amount:
            typeof editingExpense.amount === "number"
              ? editingExpense.amount
              : parseFloat(String(editingExpense.amount)) || 0,
          recurring:
            (editingExpense.recurring as "one-time" | "monthly" | "quarterly" | "yearly") ||
            "one-time",
          isVirtual: editingExpense.isVirtual || false,
          isIncome: editingExpense.isIncome || false,
          nextPaymentDate: editingExpense.nextPaymentDate || new Date().toISOString(),
          expenseCategoryId: editingExpense.expenseCategoryId,
          splitPreference,
          splits,
        } as ExpenseFormData)
      }

      populateForm()
    }
  }, [editingExpense, showDialog, partners, reset])

  const onSubmit = useCallback(
    async (data: ExpenseFormData) => {
      try {
        const splitPreference = data.splitPreference || "equitable"

        // Only include splits if split preference is "custom"
        let processedSplits: typeof data.splits = undefined
        if (splitPreference === "custom" && data.splits && data.splits.length > 0) {
          processedSplits = data.splits.map((split) => ({
            ...split,
            splitType: "custom" as const,
          }))
        }

        if (editingExpense) {
          const updateData: UpdateExpense = {
            name: data.name,
            description:
              data.description &&
              typeof data.description === "string" &&
              data.description.trim() !== ""
                ? data.description
                : "",
            amount: data.amount,
            recurring: data.recurring || undefined,
            isVirtual: data.isVirtual || false,
            isIncome: "isIncome" in data ? data.isIncome || false : false,
            splitPreference: splitPreference,
            nextPaymentDate:
              data.recurring && data.recurring !== "one-time" && data.nextPaymentDate
                ? typeof data.nextPaymentDate === "string"
                  ? data.nextPaymentDate
                  : undefined
                : undefined,
            expenseCategoryId:
              typeof data.expenseCategoryId === "number" ? data.expenseCategoryId : undefined,
            splits: processedSplits,
          }
          await onUpdateExpense(editingExpense.id, updateData)
        } else {
          const createData: CreateExpense = {
            name: data.name!,
            description:
              data.description &&
              typeof data.description === "string" &&
              data.description.trim() !== ""
                ? data.description
                : undefined,
            amount: data.amount!,
            recurring: data.recurring || "one-time",
            isVirtual: data.isVirtual || false,
            isIncome: "isIncome" in data ? data.isIncome || false : false,
            splitPreference: splitPreference,
            nextPaymentDate:
              data.recurring && data.recurring !== "one-time" && data.nextPaymentDate
                ? typeof data.nextPaymentDate === "string"
                  ? data.nextPaymentDate
                  : new Date().toISOString()
                : new Date().toISOString(),
            expenseCategoryId: data.expenseCategoryId as number,
            splits: processedSplits,
          }
          await onCreateExpense(createData)
        }
        onCloseDialog()
      } catch (error) {
        console.error("Error saving expense:", error)
      }
    },
    [editingExpense, onUpdateExpense, onCreateExpense, onCloseDialog]
  )

  const handleDelete = useCallback(async () => {
    if (!deletingExpense) return
    try {
      await onDeleteExpense(deletingExpense.id)
      onCloseDeleteDialog()
    } catch (error) {
      console.error("Error deleting expense:", error)
    }
  }, [deletingExpense, onDeleteExpense, onCloseDeleteDialog])

  return (
    <>
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          if (!open) {
            onCloseDialog()
          }
        }}
        title={
          editingExpense
            ? t("components.expenses.dialogs.editTitle")
            : t("components.expenses.dialogs.addTitle")
        }
        size="lg"
        description={
          editingExpense
            ? t("components.expenses.dialogs.editDescription")
            : t("components.expenses.dialogs.addDescription")
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCloseDialog}>
              {t("components.expenses.dialogs.cancel")}
            </Button>
            <Button
              type="submit"
              form="expense-form"
              disabled={isCreating || isUpdating || !isOwner}
              title={!isOwner ? t("components.expenses.tooltips.actionDisabled") : undefined}
            >
              {editingExpense
                ? t("components.expenses.dialogs.update")
                : t("components.expenses.dialogs.create")}
            </Button>
          </div>
        }
      >
        <form
          id="expense-form"
          onSubmit={handleSubmit(onSubmit, (errors) => {
            console.error("Form validation errors:", errors)
          })}
          className="flex flex-col flex-1 min-h-0"
        >
          <ErrorAlert error={error} onDismiss={() => {}} />
          <ExpenseForm
            control={control}
            errors={errors}
            categories={categories}
            recurringValue={recurringValue}
            partners={partners}
            watch={watch}
            setValue={setValue}
          />
        </form>
      </Dialog>

      <Dialog
        open={!!deletingExpense}
        onOpenChange={(open) => {
          if (!open) {
            onCloseDeleteDialog()
          }
        }}
        title={t("components.expenses.dialogs.deleteTitle")}
        description={
          deletingExpense
            ? t("components.expenses.dialogs.deleteDescription", {
                name: deletingExpense.name,
              })
            : ""
        }
      >
        <DialogActions
          onCancel={onCloseDeleteDialog}
          onConfirm={handleDelete}
          cancelText={t("components.expenses.dialogs.cancel")}
          confirmText={t("components.expenses.dialogs.delete")}
          confirmVariant="destructive"
          isLoading={isDeleting}
          isConfirmDisabled={!isOwner}
          confirmTooltip={!isOwner ? t("components.expenses.tooltips.actionDisabled") : undefined}
        />
      </Dialog>
    </>
  )
}
